import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

/**
 * Small install prompt shown on the Guidance page after the first minute
 * on-site. Uses the browser's `beforeinstallprompt` event (Chrome / Edge /
 * Samsung Internet). Also shows a fallback for iOS Safari with the
 * Share → Add to Home Screen instructions.
 *
 * Dismissed prompts remember their state in localStorage for 30 days.
 */
const DISMISS_KEY = "ep_pwa_dismissed_at";
const DISMISS_MS = 30 * 24 * 3600 * 1000; // 30 days

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [installEvent, setInstallEvent] = useState(null);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    // Skip if user dismissed within the last 30 days
    try {
      const dismissed = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (dismissed && Date.now() - dismissed < DISMISS_MS) return;
    } catch (e) { /* ignore quota */ }

    const onBIP = (e) => {
      e.preventDefault();
      setInstallEvent(e);
      // Reveal after 45 seconds so we don't ambush first-time visitors
      setTimeout(() => setVisible(true), 45_000);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // iOS Safari: no beforeinstallprompt — show hint after 60s
    if (isIOS()) {
      const t = setTimeout(() => {
        setShowIOSHint(true);
        setVisible(true);
      }, 60_000);
      return () => {
        window.removeEventListener("beforeinstallprompt", onBIP);
        clearTimeout(t);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBIP);
  }, []);

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const result = await installEvent.userChoice.catch(() => ({ outcome: "dismissed" }));
    if (result.outcome === "accepted") {
      setVisible(false);
    }
    setInstallEvent(null);
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible || (!installEvent && !showIOSHint)) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed z-50 left-4 right-4 bottom-24 md:bottom-6 md:left-auto md:right-6 md:max-w-sm rounded-2xl border border-[color:var(--gold)]/40 p-4 md:p-5 shadow-xl"
      style={{ background: "linear-gradient(180deg, rgba(15,25,55,0.98), rgba(10,17,40,0.98))" }}
    >
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss install prompt"
        data-testid="install-dismiss"
        className="absolute top-3 right-3 text-[color:var(--text-secondary)] hover:text-[color:var(--parchment)]"
      >
        <X size={16} />
      </button>
      <div className="flex items-start gap-3">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(212,162,76,0.15)" }}
        >
          <Smartphone size={20} className="text-[color:var(--gold)]" />
        </div>
        <div className="min-w-0">
          <div className="label-overline text-[color:var(--gold)]/85">Install</div>
          <div className="mt-1 font-serif-display text-[color:var(--parchment)] text-lg leading-tight">
            Keep Earth Pulse on your home screen
          </div>
          {showIOSHint ? (
            <div className="mt-2 font-serif-italic text-[color:var(--text-secondary)] text-sm leading-relaxed">
              Tap the <span className="text-[color:var(--gold)]">Share</span> button below, then{" "}
              <span className="text-[color:var(--gold)]">Add to Home Screen</span>.
            </div>
          ) : (
            <button
              type="button"
              onClick={install}
              data-testid="install-accept"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium text-sm"
            >
              <Download size={14} /> Install app
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
